import { useState, useEffect } from 'react';
import { useSubscribers } from '../../../../hooks/useSubscribers';
import { useSubcontractors } from '../../../../hooks/useSubcontractors';

// Estado inicial para suscriptores del proyecto
const INITIAL_PROJECT_SUBSCRIBER_STATE = {
  uuid_subscriber: '',
  subscriber: null,
  role: 'owner',
  assigned_date: new Date().toISOString().split('T')[0],
};

/**
 * Hook personalizado para el manejo del ProjectDialog
 * Siguiendo el patrón del useContractIndividualEdit
 */
export const useProjectDialog = ({ selectedProject, open, onSave, editMode }) => {
  const [localProject, setLocalProject] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const [subscriberDialogOpen, setSubscriberDialogOpen] = useState(false);
  const [selectedSubscriber, setSelectedSubscriber] = useState(INITIAL_PROJECT_SUBSCRIBER_STATE);
  const [editModeSubscriber, setEditModeSubscriber] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [matchingSubscribers, setMatchingSubscribers] = useState([]);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [modal, setModal] = useState({
    open: false,
    message: '',
  });

  const { data: subscribers = [] } = useSubscribers();
  const { data: subcontractors = [], isLoading: subcontractorsLoading } = useSubcontractors();

  const toMonthValue = (value) => {
    if (!value) return '';
    if (typeof value === 'string') {
      // YYYY-MM-DD or YYYY-MM => YYYY-MM
      const isoMatch = value.match(/^(\d{4})-(\d{2})/);
      if (isoMatch) {
        return `${isoMatch[1]}-${isoMatch[2]}`;
      }
      // DD/MM/YYYY => YYYY-MM
      const parts = value.split('/');
      if (parts.length === 3) {
        const [day, month, year] = parts;
        if (year && month) {
          return `${year}-${month.padStart(2, '0')}`;
        }
      }
      return '';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  useEffect(() => {
    if (open) {
      if (editMode && selectedProject) {
        setLocalProject({
          ...selectedProject,
          start_date: toMonthValue(selectedProject.start_date),
          end_date: toMonthValue(selectedProject.end_date),
        });
      } else {
        setLocalProject({
          uuid_lot: null,
          uuid_subcontractor: '',
          name: '',
          description: '',
          status: 'not_started',
          construction_type: 'not_assigned',
          start_date: '',
          end_date: '',
          progress_template: '',
          quality_template: '',
        });
      }
      setActiveTab(0);
    }
  }, [open, editMode, selectedProject]);

  // Efecto para buscar suscriptores basado en el input de búsqueda
  useEffect(() => {
    if (searchInput.length >= 3) {
      const matching = subscribers.filter(subscriber =>
        subscriber.dni?.includes(searchInput) ||
        subscriber.first_name?.toLowerCase().includes(searchInput.toLowerCase()) ||
        subscriber.last_name?.toLowerCase().includes(searchInput.toLowerCase())
      );
      setMatchingSubscribers(matching);
    } else {
      setMatchingSubscribers([]);
    }
  }, [searchInput, subscribers]);

  // Handlers para campos del proyecto
  const handleProjectChange = (field, value) => {
    setLocalProject(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handler para inputs de texto
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    handleProjectChange(name, value);
  };

  // Handlers para tabs
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handlers para suscriptores
  const handleAddSubscriber = () => {
    setSelectedSubscriber(INITIAL_PROJECT_SUBSCRIBER_STATE);
    setEditModeSubscriber(false);
    setSearchInput('');
    setMatchingSubscribers([]);
    setSubscriberDialogOpen(true);
  };

  const handleEditSubscriber = (subscriber) => {
    setSelectedSubscriber(subscriber);
    setEditModeSubscriber(true);
    setSearchInput('');
    setMatchingSubscribers([]);
    setSubscriberDialogOpen(true);
  };

  const handleDeleteSubscriber = (subscriberId) => {
    setLocalProject(prev => ({
      ...prev,
      project_subscribers: prev.project_subscribers.filter(
        sub => sub.uuid_subscriber !== subscriberId
      ),
    }));
    showSnackbar('Suscriptor eliminado correctamente', 'success');
  };

  const handleSubscriberSave = (subscriberData) => {
    if (editModeSubscriber) {
      setLocalProject(prev => ({
        ...prev,
        project_subscribers: prev.project_subscribers.map(sub =>
          sub.uuid_subscriber === subscriberData.uuid_subscriber
            ? subscriberData
            : sub
        ),
      }));
      showSnackbar('Suscriptor actualizado correctamente', 'success');
    } else {
      const newProjectSubscribers = [...(localProject?.project_subscribers || []), subscriberData];
      
      setLocalProject(prev => ({
        ...prev,
        project_subscribers: newProjectSubscribers,
      }));
      showSnackbar('Suscriptor agregado correctamente', 'success');
    }
    setSubscriberDialogOpen(false);
  };

  const handleSubscriberSearch = (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setMatchingSubscribers([]);
      return;
    }

    const filtered = subscribers.filter(subscriber => {
      const fullName = `${subscriber.first_name} ${subscriber.last_name}`.toLowerCase();
      const dni = subscriber.dni?.toLowerCase() || '';
      const email = subscriber.email?.toLowerCase() || '';
      const search = searchTerm.toLowerCase();
      
      return fullName.includes(search) || 
             dni.includes(search) || 
             email.includes(search);
    });

    setMatchingSubscribers(filtered);
  };

  // Validación del formulario
  const validateForm = () => {
    const errors = [];

    if (!localProject?.uuid_lot) {
      errors.push('Debes seleccionar un lote');
    }

    if (editMode && !localProject?.name?.trim()) {
      errors.push('El nombre del proyecto es requerido');
    }

    if (!localProject?.start_date) {
      errors.push('La fecha de inicio es requerida');
    }

    if (!editMode) {
      if (!localProject?.progress_template) {
        errors.push('Debe seleccionar un template de progreso');
      }
      if (!localProject?.quality_template) {
        errors.push('Debe seleccionar un template de calidad');
      }
    }

    if (localProject?.end_date && localProject?.start_date) {
      const startDate = new Date(localProject.start_date);
      const endDate = new Date(localProject.end_date);
      
      if (endDate < startDate) {
        errors.push('La fecha de fin no puede ser anterior a la fecha de inicio');
      }
    }

    if (errors.length > 0) {
      showSnackbar(errors.join('. '), 'error');
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const normalizeMonthToDate = (value) => {
        if (!value) return null;
        if (typeof value !== 'string') return value;
        // If already YYYY-MM-DD, keep it
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          return value;
        }
        // If month input YYYY-MM, convert to last day of that month
        const monthMatch = value.match(/^(\d{4})-(\d{2})$/);
        if (monthMatch) {
          const year = Number(monthMatch[1]);
          const month = Number(monthMatch[2]);
          if (!Number.isNaN(year) && !Number.isNaN(month)) {
            const lastDay = new Date(year, month, 0).getDate();
            return `${monthMatch[1]}-${monthMatch[2]}-${String(lastDay).padStart(2, '0')}`;
          }
        }
        return value;
      };

      const projectData = {
        ...localProject,
        start_date: normalizeMonthToDate(localProject.start_date),
        end_date: normalizeMonthToDate(localProject.end_date),
      };
      if (!editMode && projectData.uuid_subcontractor === '') {
        projectData.uuid_subcontractor = null;
      }

      console.log('Enviando datos del proyecto:', projectData); // Debug log
      
      await onSave(projectData);
      
      showSnackbar(
        editMode ? 'Proyecto actualizado correctamente' : 'Proyecto creado correctamente',
        'success'
      );
    } catch (error) {
      console.error('Error al guardar proyecto:', error); // Debug log
      showSnackbar(
        error.message || 'Error al guardar el proyecto', 
        'error'
      );
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const showModal = (message) => {
    setModal({
      open: true,
      message,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleCloseModal = () => {
    setModal(prev => ({ ...prev, open: false }));
  };

  const getAvailableSubscribers = () => {
    const assignedIds = localProject?.project_subscribers?.map(ps => ps.uuid_subscriber) || [];
    return subscribers.filter(subscriber => !assignedIds.includes(subscriber.uuid));
  };

  const handleSelectChange = (event) => {
    const { name, value } = event.target;
    handleProjectChange(name, value);
  };

  const handleOpenSubscriberDialog = handleAddSubscriber;
  const handleCloseSubscriberDialog = () => {
    setSubscriberDialogOpen(false);
    setSelectedSubscriber(INITIAL_PROJECT_SUBSCRIBER_STATE);
    setSearchInput('');
    setMatchingSubscribers([]);
  };
  
  const handleSaveSubscriber = () => {
    const subscriberData = {
      uuid_subscriber: selectedSubscriber.uuid_subscriber,
      subscriber: selectedSubscriber.subscriber,
      role: selectedSubscriber.role,
      assigned_date: selectedSubscriber.assigned_date,
    };
    
    handleSubscriberSave(subscriberData);
  };
  const handleSaveProject = handleSave;

  const handleInputChangeSubscriber = (event) => {
    const { name, value } = event.target;
    setSelectedSubscriber(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubscriberSelection = (subscriber) => {
    setSelectedSubscriber(prev => ({
      ...prev,
      subscriber: subscriber,
      uuid_subscriber: subscriber ? subscriber.uuid : '',
    }));
    setSearchInput(subscriber ? subscriber.dni : '');
  };

  const handleInputChangeDNISubscriber = (event) => {
    const value = event.target.value;
    setSearchInput(value);
  };

  return {
    // Estados principales
    localProject,
    activeTab,
    setActiveTab,

    // Estados del diálogo de suscriptor
    subscriberDialogOpen,
    setSubscriberDialogOpen,
    selectedSubscriber,
    editModeSubscriber,
    searchInput,
    matchingSubscribers,

    // Estados de notificaciones
    snackbar,
    modal,

    // Handlers principales
    handleProjectChange,
    handleInputChange,
    handleSelectChange,
    handleTabChange,
    handleSave,
    handleSaveProject,

    // Handlers de suscriptores
    handleAddSubscriber,
    handleOpenSubscriberDialog,
    handleEditSubscriber,
    handleDeleteSubscriber,
    handleSubscriberSave,
    handleSaveSubscriber,
    handleSubscriberSearch,
    handleCloseSubscriberDialog,
    handleInputChangeSubscriber,
    handleSubscriberSelection,
    handleInputChangeDNISubscriber,

    // Handlers de notificaciones
    handleCloseSnackbar,
    handleCloseModal,

    // Helpers
    getAvailableSubscribers,
    validateForm,
    showSnackbar,
    showModal,

    // Datos
    subscribers,
    subcontractors,
    subcontractorsLoading,
  };
};
